/**
 * useSubscriptionPlans — W2.6
 *
 * Ruft GET /api/v2/subscription/plans ab.
 * Gibt alle öffentlich buchbaren Pläne zurück (professional_new gefiltert vom Backend).
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

/**
 * Laufzeit-Eintrag aus GET /api/v2/subscription/plans → durations[].
 * `price` ist der GESAMTpreis der Laufzeit (inkl. Laufzeitrabatt −10/−20/−30 %)
 * in der Anzeige-Währung; `stripe_price_id` die bestehende Stripe-Preis-ID
 * für genau diese Laufzeit (Story 6.3 AC-3: bestehende Checkout-Flows).
 */
export type PlanDuration = {
  months: number
  price?: number
  stripe_price_id?: string | null
  discount_pct?: number
}

export type Plan = {
  id: string
  name: string
  // Monatspreis bereits in der angeforderten Anzeige-Währung (siehe `currency`).
  price_eur_monthly: number
  currency: string
  hotspot_limit: number
  api_access?: boolean
  stripe_price_id?: string
  campaign_active: boolean
  campaign_discount_pct: number
  features?: string[]
  durations?: PlanDuration[]
  // Doppel-Limits aus Story 6.1/6.2 (Kontingent-Chips der Pricing-Seite).
  //
  // Der PlanSerializer liefert sie als `mobile_hotspot_limit` /
  // `iot_hotspot_limit` (identische Quelle wie HotspotQuotaEnforcer:
  // Authentication::SubscriptionService::MOBILE_HOTSPOT_LIMITS/
  // IOT_HOTSPOT_LIMITS); `null` bedeutet unbegrenzt. Die `max_*`-Namen sind
  // die urspruenglich in Story 6.3 angenommenen Feldnamen und bleiben als
  // Alias bestehen, falls der Contract sie spaeter so fuehrt.
  // Fallback-Matrix (nur wenn KEIN Feld ankommt): features/premium/planQuotas.ts.
  mobile_hotspot_limit?: number | null
  iot_hotspot_limit?: number | null
  max_mobile_hotspots?: number | null
  max_iot_hotspots?: number | null
}

type UseSubscriptionPlansResult = {
  plans: Plan[] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useSubscriptionPlans(currency?: string): UseSubscriptionPlansResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any

  const query = useApi<Plan[]>(
    ['subscription', 'plans', currency ?? 'usd'],
    async () => {
      // Anzeige-Währung als Query mitgeben (Backend rechnet USD-Basis um).
      const result = await client.GET(
        '/api/v2/subscription/plans',
        currency ? { params: { query: { currency } } } : undefined,
      )
      // Backend liefert { plans: [...] }, Frontend erwartet das Array direkt
      const raw = result?.data as { plans?: Plan[] } | Plan[] | undefined
      const arr = Array.isArray(raw) ? raw : raw?.plans
      return { data: arr, error: result?.error }
    },
  )

  return {
    plans: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

/**
 * Berechnet den Gesamtpreis für einen Plan mit optionalem Kampagnenrabatt.
 *
 * Story 6.3: Liefert das Backend eine `durations`-Matrix, ist deren
 * `price` (Gesamtpreis inkl. Laufzeitrabatt) die Quelle der Wahrheit.
 *
 * Fallback (Welle-4b-Review F1, konsistent zu planQuotas.planMonthlyPrice):
 * OHNE Backend-Preis wird KEIN Rabatt gerechnet, sondern Basispreis × Monate
 * — genau der Betrag, den Stripe ohne Laufzeit-Preis-ID abbucht. Ein selbst
 * gerechneter Rabatt waere eine Falschangabe.
 */
export function displayPrice(plan: Plan, duration: number): number {
  const entry = plan.durations?.find((d) => d.months === duration)
  const total =
    entry && typeof entry.price === 'number' ? entry.price : plan.price_eur_monthly * duration
  if (plan.campaign_active) {
    return total * (1 - plan.campaign_discount_pct / 100)
  }
  return total
}
